// Traceability: FR-/AS- IDs reference
// .verified/features/dashboard-layout-editor/spec.md; ADR-022 is the grid model.

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";

import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { errorPopup } from "@/utils/alerts";

import type { DashboardView } from "./types";

export interface DashboardToolbarProps {
    views: DashboardView[];
    activeViewId: number;
    editMode: boolean;
    modified: boolean;
    onEnterEdit: () => void;
    onSave: () => void | Promise<void>;
    onSaveAs: (name: string) => void | Promise<void>;
    onReset: () => void | Promise<void>;
    onDelete: () => void | Promise<void>;
    onCancel: () => void;
    onSwitchView: (id: number) => void;
    /**
     * When false, the Edit button is disabled (the page gates editing to desktop —
     * FR-23). Defaults to true so existing callers/tests are unaffected.
     */
    canEdit?: boolean;
}

const DEFAULT_VIEW_ID = 0;

async function confirm(title: string, text: string): Promise<boolean> {
    const result = await Swal.fire({
        title,
        text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
    });
    return Boolean(result?.isConfirmed);
}

interface SaveAsDialogProps {
    open: boolean;
    views: DashboardView[];
    onClose: () => void;
    onConfirm: (name: string) => void;
}

const SaveAsDialog: React.FC<SaveAsDialogProps> = ({ open, views, onClose, onConfirm }) => {
    const [name, setName] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setName("");
            setError("");
        }
    }, [open]);

    const onSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const trimmed = name.trim();
        if (trimmed === "") {
            setError("Name is required.");
            return;
        }
        if (trimmed === "Default") {
            setError("That name is not allowed.");
            return;
        }
        if (views.some((view) => view.name === trimmed)) {
            setError("A view with that name already exists.");
            return;
        }
        onConfirm(trimmed);
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()} modal={true}>
            <DialogContent data-testid="save-view-dialog">
                <DialogHeader>
                    <DialogTitle>Save view as</DialogTitle>
                    <DialogDescription>Name this dashboard view to save its current layout.</DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="viewName">Name</Label>
                            <div className="space-y-1">
                                <Input
                                    id="viewName"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    aria-invalid={!!error}
                                    aria-describedby={error ? "viewNameError" : undefined}
                                    className={error ? "border-red-500" : ""}
                                />
                                {error && (
                                    <p id="viewNameError" className="text-sm text-red-500">
                                        {error}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="volt">
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const DashboardToolbar: React.FC<DashboardToolbarProps> = ({
    views,
    activeViewId,
    editMode,
    modified,
    onEnterEdit,
    onSave,
    onSaveAs,
    onReset,
    onDelete,
    onCancel,
    onSwitchView,
    canEdit = true,
}) => {
    const [saveAsOpen, setSaveAsOpen] = useState(false);
    const actionBarRef = useRef<HTMLDivElement>(null);

    // Default (id 0) first, then the named views in input order.
    const orderedViews = useMemo(() => {
        const def = views.find((v) => v.id === DEFAULT_VIEW_ID);
        const named = views.filter((v) => v.id !== DEFAULT_VIEW_ID);
        return def ? [def, ...named] : named;
    }, [views]);

    const isDefault = activeViewId === DEFAULT_VIEW_ID;

    // When edit mode turns on, move focus into the action bar.
    useEffect(() => {
        if (!editMode) return;
        const bar = actionBarRef.current;
        if (!bar) return;
        const focusable = bar.querySelector<HTMLElement>(
            "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
        );
        focusable?.focus();
    }, [editMode]);

    // Esc while editing: confirm if modified, then cancel.
    useEffect(() => {
        if (!editMode) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            // Let an open dialog handle its own Esc.
            if (saveAsOpen) return;
            event.preventDefault();
            if (modified) {
                void confirm("Discard changes?", "Your unsaved layout changes will be lost.").then((ok) => {
                    if (ok) onCancel();
                });
            } else {
                onCancel();
            }
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [editMode, modified, saveAsOpen, onCancel]);

    const handleSave = () => {
        if (isDefault) {
            // Default cannot be saved in place — route to Save As.
            setSaveAsOpen(true);
            return;
        }
        void Promise.resolve()
            .then(() => onSave())
            .catch(() => {
                void errorPopup("Failed to save the dashboard layout.");
            });
    };

    const handleReset = () => {
        void confirm("Reset layout?", "This restores the saved layout for this view.").then((ok) => {
            if (ok) void onReset();
        });
    };

    const handleDelete = () => {
        void confirm("Delete view?", "This permanently removes this dashboard view.").then((ok) => {
            if (ok) void onDelete();
        });
    };

    const handleSaveAsConfirm = (name: string) => {
        setSaveAsOpen(false);
        void onSaveAs(name);
    };

    return (
        <div data-testid="dashboard-toolbar" data-edit-mode={editMode ? "true" : undefined} className="flex items-center gap-2 flex-wrap">
            <div data-testid="view-selector">
                <Select value={String(activeViewId)} onValueChange={(value) => onSwitchView(Number(value))}>
                    <SelectTrigger aria-label="Dashboard view">
                        <SelectValue placeholder="Select view" />
                    </SelectTrigger>
                    <SelectContent>
                        {orderedViews.map((view) => (
                            <SelectItem key={view.id} value={String(view.id)}>
                                {view.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {editMode && modified && (
                <span data-testid="view-modified-indicator" className="text-sm text-amber-600" title="Unsaved changes">
                    ● Unsaved changes
                </span>
            )}

            {!editMode && (
                <Button data-testid="edit-dashboard-btn" variant="volt" size="sm" onClick={onEnterEdit} disabled={!canEdit}>
                    Edit dashboard
                </Button>
            )}

            {editMode && (
                <div ref={actionBarRef} className="flex items-center gap-2">
                    <Button data-testid="save-layout-btn" variant="volt" size="sm" onClick={handleSave}>
                        Save
                    </Button>
                    <Button data-testid="save-as-layout-btn" variant="secondary" size="sm" onClick={() => setSaveAsOpen(true)}>
                        Save As
                    </Button>
                    <Button data-testid="reset-layout-btn" variant="secondary" size="sm" onClick={handleReset}>
                        Reset
                    </Button>
                    {!isDefault && (
                        <Button data-testid="delete-view-btn" variant="destructive" size="sm" onClick={handleDelete}>
                            Delete
                        </Button>
                    )}
                    <Button data-testid="cancel-edit-btn" variant="ghost" size="sm" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            )}

            <SaveAsDialog open={saveAsOpen} views={views} onClose={() => setSaveAsOpen(false)} onConfirm={handleSaveAsConfirm} />
        </div>
    );
};

export default DashboardToolbar;
